<script setup>
import PrimaryButton from '@/Components/Elements/PrimaryButton.vue'
import TextInput from '@/Components/FormElements/TextInput.vue'
import PasswordInput from '@/Components/FormElements/PasswordInput.vue'
import Checkbox from '@/Components/FormElements/Checkbox.vue'
import SearchableSelect from '@/Components/FormElements/SearchableSelect.vue'
import { Link, useForm } from '@inertiajs/vue3'
import { computed, ref, onMounted } from 'vue'

const form = useForm({
    firstname: '',
    lastname: '',
    fullname: '',
    country_id: null,
    email: '',
    email_confirmation: '',
    password: '',
    password_confirmation: '',
    terms: false,
    time_to_complete: 0,
})

const startTime = ref(null)

onMounted(() => {
    startTime.value = Date.now()
})

const props = defineProps(['countries'])

const countryOptions = computed(() => {
    return props.countries.map((country) => ({
        value: country.id,
        name: country.name,
        html_icon: `<span class="fi fi-${country.iso2.toLowerCase()} w-4"/>`,
    }))
})

const submit = () => {
    form.time_to_complete = Math.floor((Date.now() - startTime.value) / 1000)
    form.post(route('register'))
}
</script>

<template>
    <div class="flex flex-col items-center justify-center flex-grow">
        <h2 class="text-text mb-8">
            {{ $lang.get('Register account') }}
        </h2>
        <form
            @submit.prevent="submit"
            class="max-w-xl w-full bg-white px-6 py-4 rounded-card border border-border shadow space-y-4 mb-4"
        >
            <div class="flex gap-x-4">
                <TextInput
                    id="name"
                    :label="$lang.get('First name')"
                    type="text"
                    v-model="form.firstname"
                    :required="true"
                    autofocus
                    autocomplete="given-name"
                    :error="form.errors.firstname"
                />
                <TextInput
                    id="last_name"
                    :label="$lang.get('Last name')"
                    type="text"
                    v-model="form.lastname"
                    required
                    autofocus
                    autocomplete="family-name"
                    :error="form.errors.lastname"
                />
                <div class="absolute top-[-100vh]">
                    <TextInput
                        id="fullname"
                        label="Full name"
                        type="text"
                        v-model="form.fullname"
                        autocomplete="user_alias"
                        autofocus
                        :error="form.errors.fullname"
                    />
                </div>
            </div>

            <SearchableSelect
                id="country"
                :label="$lang.get('Select country')"
                v-model="form.country_id"
                :options="countryOptions"
                :placeholder="$lang.get('Select country')"
                :error="form.errors.country_id"
            />

            <TextInput
                id="email"
                :label="$lang.get('Email')"
                type="email"
                class="mt-1 block w-full"
                v-model="form.email"
                required
                autocomplete="email"
                :error="form.errors.email"
            />
            <TextInput
                id="email_confirmation"
                :label="$lang.get('Confirm Email')"
                type="email"
                class="mt-1 block w-full"
                v-model="form.email_confirmation"
                required
                :error="form.errors.email_confirmation"
            />
            <div class="flex w-full gap-x-4">
                <PasswordInput
                    id="password"
                    class="w-full"
                    :label="$lang.get('Password')"
                    v-model="form.password"
                    :error="form.errors.password"
                    required
                />

                <PasswordInput
                    id="confirm_password"
                    class="w-full"
                    :label="$lang.get('Confirm Password')"
                    v-model="form.password_confirmation"
                    :error="form.errors.password_confirmation"
                    required
                />
            </div>

            <div
                v-if="$page.props.jetstream.hasTermsAndPrivacyPolicyFeature"
                class="flex items-center"
            >
                <!-- <Checkbox
                    id="remember"
                    :label="$lang.get('Remember me')"
                    name="remember"
                    v-model:checked="form.remember"
                /> -->
                <Checkbox
                    id="terms"
                    v-model:checked="form.terms"
                    name="terms"
                    required
                    :error="form.errors.terms"
                >
                    <template #checkbox-label>
                        <div class="ms-2">
                            {{ $lang.get('I agree to the') }}
                            <Link
                                target="_blank"
                                :href="route('terms.show')"
                                class="underline text-sm text-text-secondary font-medium underline-hover focus-style"
                                v-text="$lang.get('Terms of Service')"
                            />
                            {{ $lang.get('and') }}
                            <Link
                                target="_blank"
                                :href="route('policy.show')"
                                class="underline text-sm text-text-secondary font-medium underline-hover focus-style"
                                v-text="$lang.get('Privacy Policy')"
                            />
                        </div>
                    </template>
                </Checkbox>
            </div>

            <div class="flex items-center justify-between mt-4 font-header">
                <Link
                    :href="route('login')"
                    class="underline-hover text-text-secondary rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-border-border-active"
                    v-text="$lang.get('Already registered?')"
                />

                <PrimaryButton
                    type="submit"
                    class="ms-4"
                    weight="font-semibold"
                    :class="{ 'opacity-25': form.processing }"
                    :disabled="form.processing"
                    v-text="$lang.get('Register')"
                />
            </div>
        </form>
    </div>
</template>
